import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  useStationMarkers,
  useCreateStationMarker,
  usePatchStationMarker,
  useDeleteStationMarker
} from '../hooks/useStationMarkers'
import { useArchived } from '../contexts/ArchiveContext'
import { StationMarkerEditor } from './StationMarkerEditor'
import { KIND_META, GROUP_LABELS, GROUP_ORDER } from './stationMarkerKinds'
import { STATION_SLUGS } from '../types'
import type { StationMapManifest, StationMarker, StationSlug } from '../types'

// StationMapTab — per-event station site maps (spec 31 / item 7).
//
// Layout:
//   1. Header + station chip strip (Pickering / Levisham / Goathland / Grosmont).
//   2. Map canvas — the station's base image with markers overlaid as
//      circular pins at their normalised (x, y) positions. Click an
//      empty spot → opens the editor in create mode with that x/y;
//      click a pin → opens the editor in edit mode. Markers stay
//      pinned to the same image-relative position at any zoom level
//      (CSS percentage positioning against the image's container).
//   3. Legend showing the marker kinds currently in use on this
//      station with a count per kind.
//
// Source of base images: public/station-maps/manifest.json — generated
// Static images committed to public/station-maps/, declared in manifest.json. Stations
// without a manifest entry render an empty state explaining what's
// needed to enable that station.

interface Props {
  eventId: number
}

export function StationMapTab({ eventId }: Props) {
  const archived = useArchived()
  // Manifest is a static asset — fetch once, cache forever (server-
  // side cache busts via the JS hash). Validates the response shape
  // defensively: if SWA's navigation fallback ever swallows the
  // request and returns index.html, or the file is missing in deploy,
  // we want a clean "manifest unavailable" state, not a JSON parse
  // explosion that propagates a render error up the tree.
  const { data: manifest } = useQuery({
    queryKey: ['station-map-manifest'],
    queryFn: async (): Promise<StationMapManifest | null> => {
      try {
        const res = await fetch('/station-maps/manifest.json')
        if (!res.ok) return null
        // Guard against the SWA SPA rewrite returning HTML in place
        // of the JSON. The exclude rule in staticwebapp.config.json
        // should prevent this, but a malformed config or a missing
        // exclude on a future env would silently break — return null
        // rather than letting JSON.parse throw and trip the catch.
        const text = await res.text()
        if (!text.trim().startsWith('{')) return null
        const parsed = JSON.parse(text) as unknown
        if (!parsed || typeof parsed !== 'object') return null
        const m = parsed as Partial<StationMapManifest>
        if (!m.stations || typeof m.stations !== 'object') return null
        return m as StationMapManifest
      } catch {
        return null
      }
    },
    staleTime: Infinity,
    // Don't retry the manifest fetch — if it returned the wrong
    // content type once, the next 3 attempts will too. Failing
    // quickly to "no manifest available" lets the UI render a
    // useful empty state instead of spinning.
    retry: false
  })

  // Default to the first station the manifest knows about; falls back
  // to 'pickering' so the chip strip always has something selected
  // even pre-manifest-load. Optional-chained access on manifest.stations
  // so a malformed/null manifest never throws here.
  const knownSlugs = useMemo<StationSlug[]>(() => {
    if (!manifest) return ['pickering']
    return STATION_SLUGS.filter((s) => manifest.stations?.[s] != null)
  }, [manifest])

  const [selected, setSelected] = useState<StationSlug>('pickering')
  // If the selected station has no manifest entry but other stations
  // do, switch to the first available so we don't lock the user on an
  // empty state.
  useEffect(() => {
    if (knownSlugs.length > 0 && !knownSlugs.includes(selected) && knownSlugs[0]) {
      setSelected(knownSlugs[0])
    }
  }, [knownSlugs, selected])

  const { data: markers, isLoading } = useStationMarkers(eventId, selected)
  const createMut = useCreateStationMarker(eventId)
  const patchMut  = usePatchStationMarker(eventId)
  const deleteMut = useDeleteStationMarker(eventId)

  // Editor state: 'new' with x/y position OR a specific marker OR null.
  type EditorState = { mode: 'new'; x: number; y: number } | { mode: 'edit'; marker: StationMarker } | null
  const [editor, setEditor] = useState<EditorState>(null)

  // Click-to-place: translate the click event's (clientX, clientY) into
  // normalised (x, y) ∈ [0,1] against the image's bounding rect, then
  // open the editor in create mode. Ignore clicks that came from a
  // pin (pins stopPropagation on their own click handler).
  function handleCanvasClick(e: React.MouseEvent<HTMLDivElement>) {
    if (archived) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top)  / rect.height
    if (x < 0 || x > 1 || y < 0 || y > 1) return
    setEditor({ mode: 'new', x, y })
  }

  // Optional-chained access — manifest may be null (fetch failed or
  // returned non-JSON), stations may be missing on a malformed payload.
  const stationCfg = manifest?.stations?.[selected]
  const stationHasImage = stationCfg != null
  const visibleMarkers = markers ?? []

  // Legend: count occurrences of each kind in the visible set so the
  // legend tells the user "this map has 3 staff posts, 1 first aid…"
  const kindCounts = useMemo(() => {
    const c = new Map<typeof visibleMarkers[number]['kind'], number>()
    for (const m of visibleMarkers) c.set(m.kind, (c.get(m.kind) ?? 0) + 1)
    return c
  }, [visibleMarkers])

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="font-display text-xl">Station map</h2>
        <p className="text-sm text-ink-soft mt-0.5">
          Pin facilities, teams, items, and zones on the station site map. Click
          an empty spot to place a marker; click an existing pin to edit or delete it.
        </p>
      </div>

      {/* Station chip strip */}
      <div className="flex flex-wrap items-center gap-2" role="tablist">
        {STATION_SLUGS.map((slug) => {
          const available = manifest?.stations?.[slug] != null
          const active = slug === selected
          return (
            <button
              key={slug}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => available && setSelected(slug)}
              disabled={!available}
              title={available ? undefined : 'No base map yet — add the image to public/station-maps/ and the dimensions to manifest.json.'}
              className={
                'inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full border transition-colors capitalize ' +
                (active
                  ? 'bg-green text-parchment border-green'
                  : available
                    ? 'bg-white text-ink-soft border-line hover:bg-parchment hover:text-ink'
                    : 'bg-parchment/40 text-ink-soft/60 border-line/60 cursor-not-allowed')
              }
            >
              {slug}
              {!available && <span className="text-[10px] font-mono">(no map)</span>}
            </button>
          )
        })}
      </div>

      {/* Map canvas */}
      {!stationHasImage ? (
        <div className="bg-white border border-line rounded p-10 text-center text-sm text-ink-soft">
          <div className="font-display text-lg mb-1 text-ink">No base map for {selected} yet</div>
          <p>
            Add the image to public/station-maps/ and an entry to{' '}
            <code className="font-mono text-[11px]">public/station-maps/manifest.json</code>{' '}
            (width/height per the actual image). The image lives at{' '}
            <code className="font-mono text-[11px]">/public/station-maps/{selected}.jpg</code>.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-line rounded overflow-hidden">
          {/* Desktop max-width capped at 1000px (mx-auto centers):
              max-w-[1000px] caps desktop width, w-full lets the panel
              shrink on narrower viewports, aspectRatio tracks each station's natural
              image dimensions. object-contain lets the base image fit
              inside without cropping regardless of its aspect ratio
              (landscape Pickering, future portrait/square stations).
              Marker pins are absolutely positioned in % against the
              container, so they stay anchored at any scale. */}
          <div
            onClick={handleCanvasClick}
            className={
              'relative w-full max-w-[1000px] mx-auto select-none ' +
              (archived ? '' : 'cursor-crosshair')
            }
            style={{ aspectRatio: `${stationCfg.width} / ${stationCfg.height}` }}
          >
            <img
              src={stationCfg.imagePath}
              alt={`${selected} station site map — ${stationCfg.description}`}
              className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              draggable={false}
            />
            {visibleMarkers.map((m) => (
              <MarkerPin
                key={m.id}
                marker={m}
                onClick={archived ? undefined : () => setEditor({ mode: 'edit', marker: m })}
              />
            ))}
          </div>
          <div className="px-3 py-2 text-[11px] text-ink-soft border-t border-line bg-parchment/40 flex items-center gap-2">
            <span className="font-mono">{visibleMarkers.length} marker{visibleMarkers.length === 1 ? '' : 's'}</span>
            <span className="ml-auto">
              Base map © <a
                href={manifest?.sourceUrl ?? 'https://www.openstreetmap.org/copyright'}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-ink"
              >OpenStreetMap contributors</a>
            </span>
          </div>
        </div>
      )}

      {/* Loading + legend */}
      {isLoading && (
        <div className="text-xs text-ink-soft font-mono">Loading markers…</div>
      )}

      {visibleMarkers.length > 0 && (
        <div className="bg-white border border-line rounded p-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft mb-2">In use on this map</div>
          <div className="flex flex-wrap gap-3 text-xs">
            {GROUP_ORDER.map((g) => {
              // Use the safe metaFor() helper so an unknown kind from
              // the server doesn't crash the legend either.
              const kindsInGroup = Array.from(kindCounts.entries())
                .filter(([k]) => metaFor(k).group === g)
              if (kindsInGroup.length === 0) return null
              return (
                <div key={g} className="flex flex-wrap items-center gap-2">
                  <span className="text-ink-soft uppercase tracking-wider text-[10px] font-semibold">{GROUP_LABELS[g]}</span>
                  {kindsInGroup.map(([k, count]) => {
                    const meta = metaFor(k)
                    return (
                      <span
                        key={k}
                        className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-parchment border border-line"
                      >
                        <span
                          aria-hidden
                          className="w-3 h-3 rounded-full flex items-center justify-center text-[8px] text-parchment"
                          style={{ background: meta.colour }}
                        >
                          {meta.glyph}
                        </span>
                        <span className="text-ink">{meta.label}</span>
                        <span className="text-ink-soft font-mono">{count}</span>
                      </span>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {!archived && editor != null && (
        <StationMarkerEditor
          mode={editor.mode === 'new' ? 'create' : 'edit'}
          initial={editor.mode === 'edit' ? editor.marker : null}
          initialPosition={editor.mode === 'new' ? { x: editor.x, y: editor.y } : undefined}
          saving={createMut.isPending || patchMut.isPending}
          deleting={deleteMut.isPending}
          serverError={(createMut.error ?? patchMut.error ?? deleteMut.error) as Error | null}
          onClose={() => setEditor(null)}
          onSave={(body) => {
            if (editor.mode === 'new') {
              createMut.mutate(
                {
                  station: selected,
                  kind: body.kind,
                  label: body.label,
                  description: body.description,
                  x: editor.x,
                  y: editor.y
                },
                { onSuccess: () => setEditor(null) }
              )
            } else {
              patchMut.mutate(
                { id: editor.marker.id, body },
                { onSuccess: () => setEditor(null) }
              )
            }
          }}
          onDelete={editor.mode === 'edit' ? () => {
            const m = editor.marker
            const ok = window.confirm(`Delete ${metaFor(m.kind).label} "${m.label}"? This can't be undone.`)
            if (!ok) return
            deleteMut.mutate(m.id, { onSuccess: () => setEditor(null) })
          } : undefined}
        />
      )}
    </div>
  )
}

// ---- Marker pin ----------------------------------------------------------

// Fallback meta for unknown kinds. Prevents a crash if the backend
// returns a kind that drifted out of the frontend's STATION_MARKER_KINDS
// enum (e.g. a new migration widened the CK constraint and the frontend
// hasn't redeployed yet).
const UNKNOWN_KIND_META = {
  label: 'Marker',
  group: 'people' as const,
  glyph: '?',
  colour: '#5a5a5a'
}

function metaFor(kind: StationMarker['kind']) {
  return KIND_META[kind] ?? UNKNOWN_KIND_META
}

// Absolute-positioned dot at (x, y) ∈ [0,1] of the parent. Translate
// -50% to centre the dot on the click target rather than top-left
// anchor it.
function MarkerPin({
  marker, onClick
}: {
  marker: StationMarker
  onClick: (() => void) | undefined
}) {
  const meta = metaFor(marker.kind)
  const interactive = onClick != null
  return (
    <button
      type="button"
      onClick={interactive ? (e) => { e.stopPropagation(); onClick!() } : undefined}
      disabled={!interactive}
      title={`${meta.label}: ${marker.label}${marker.description ? ' — ' + marker.description : ''}`}
      aria-label={`${meta.label}: ${marker.label}`}
      className={
        'absolute w-7 h-7 rounded-full text-parchment text-sm font-bold flex items-center justify-center shadow-lg border-2 border-white transition-transform focus:outline-none ' +
        (interactive ? 'hover:scale-110 focus:ring-2 focus:ring-green/40' : 'cursor-default')
      }
      style={{
        left: `${marker.x * 100}%`,
        top:  `${marker.y * 100}%`,
        transform: 'translate(-50%, -50%)',
        background: meta.colour
      }}
    >
      <span aria-hidden>{meta.glyph}</span>
    </button>
  )
}
